import { Bug, CheckCircle2, ChevronDown, ChevronRight, XCircle } from "lucide-react";
import { useState } from "react";

import { getRuntimeConfig } from "@/config/runtimeConfig";
import type { DiscoveryDiagnostics } from "@/services/graphService";
import type { AppEntitlement, EntitlementResolution } from "@/types/app";
import type { DecodedJwtPayload } from "@/types/token";
import type { AccountInfo } from "@azure/msal-browser";

type Props = {
  diagnostics: DiscoveryDiagnostics;
  resolution: EntitlementResolution;
  account: AccountInfo | null;
  claims: DecodedJwtPayload | null;
};

export function DiscoveryDebugPanel({
  diagnostics,
  resolution,
  account,
  claims,
}: Props) {
  const cfg = getRuntimeConfig();

  return (
    <section className="card border-amber-300 bg-amber-50/60 p-5 text-sm text-slate-800">
      <header className="mb-3 flex items-center gap-2">
        <Bug className="h-4 w-4 text-amber-700" />
        <h2 className="font-semibold text-amber-900">
          Developer troubleshoot panel
        </h2>
        <span className="chip bg-amber-200 text-amber-900">
          ?who=seanmehrabi&amp;troubleshoot=1 (any route)
        </span>
      </header>

      <p className="mb-4 text-xs text-amber-900/80">
        Shown when the URL includes the developer handle{" "}
        <code>seanmehrabi</code> and <code>troubleshoot=1</code> on any
        page. Explains why Enterprise Applications are or are not visible
        for this user.
      </p>

      <div className="mb-4 rounded border border-amber-200 bg-white p-3">
        <div className="text-xs uppercase tracking-wide text-slate-500">Summary</div>
        <div className="mt-1 font-medium text-slate-900">
          {diagnostics.summary}
        </div>
      </div>

      <Collapsible title="Signed-in user" defaultOpen>
        <KV k="name" v={account?.name} />
        <KV k="username (UPN)" v={account?.username} />
        <KV k="tenantId" v={account?.tenantId} />
        <KV k="homeAccountId" v={account?.homeAccountId} />
        <KV k="sub" v={claims?.sub as string | undefined} />
        <KV k="oid" v={claims?.oid as string | undefined} />
        <KV k="preferred_username" v={claims?.preferred_username as string | undefined} />
        <KV k="aud" v={claims?.aud as string | undefined} />
      </Collapsible>

      <Collapsible title="Runtime configuration">
        <KV k="authority" v={cfg.authority} />
        <KV k="knownAuthority" v={cfg.knownAuthority} />
        <KV k="clientId" v={cfg.clientId} />
        <KV k="graph scopes (requested)" v={diagnostics.tokenScopesRequested.join(" ")} />
        <KV k="tenant kind (detected)" v={diagnostics.tenantKind} />
      </Collapsible>

      <Collapsible title={`Graph discovery — ${diagnostics.calls.length} call(s)`}>
        {diagnostics.tokenAcquired ? (
          <div className="text-xs text-emerald-800">
            ✓ Graph access token acquired.
          </div>
        ) : (
          <div className="text-xs text-red-800">
            ✗ No Graph access token.{" "}
            {diagnostics.tokenError ?? "acquireTokenSilent returned null."}
          </div>
        )}
        <div className="mt-3 space-y-2">
          {diagnostics.calls.length === 0 ? (
            <div className="text-xs text-slate-500">
              No HTTP calls were made. The user likely isn't signed in
              or no Graph token was available.
            </div>
          ) : (
            diagnostics.calls.map((c, idx) => (
              <div
                key={`${c.url}-${idx}`}
                className="rounded border border-slate-200 bg-white p-2 text-xs"
              >
                <div className="flex items-center gap-2">
                  {c.ok ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                  ) : (
                    <XCircle className="h-3.5 w-3.5 text-red-600" />
                  )}
                  <span className="font-semibold">{c.label}</span>
                  <span
                    className={
                      c.ok
                        ? "chip bg-emerald-100 text-emerald-800"
                        : "chip bg-red-100 text-red-800"
                    }
                  >
                    {c.status ?? "network error"}
                  </span>
                  <span className="ml-auto text-[10px] text-slate-400">
                    {c.durationMs} ms
                  </span>
                </div>
                <div className="mt-1 break-all font-mono text-[10px] text-slate-500">
                  {c.method} {c.url}
                </div>
                {c.errorCode || c.errorMessage ? (
                  <div className="mt-1 text-[11px] text-red-800">
                    {c.errorCode ? (
                      <span className="font-semibold">{c.errorCode}: </span>
                    ) : null}
                    {c.errorMessage}
                  </div>
                ) : null}
                {typeof c.itemCount === "number" ? (
                  <div className="mt-1 text-[11px] text-slate-600">
                    {c.itemCount} item(s) returned.
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      </Collapsible>

      <Collapsible
        title={`Catalog entitlement — ${resolution.allowed.length} visible, ${resolution.denied.length} hidden`}
      >
        {resolution.allowed.length + resolution.denied.length === 0 ? (
          <div className="text-xs text-slate-500">
            The catalog is empty. Graph returned no assignments and
            VITE_APPS_JSON has no tiles.
          </div>
        ) : (
          <>
            {resolution.allowed.length > 0 ? (
              <div className="mb-2">
                <div className="text-xs font-semibold text-emerald-800">
                  Visible
                </div>
                <ul className="mt-1 space-y-1">
                  {resolution.allowed.map((e) => (
                    <EntitlementRow key={e.appId} e={e} />
                  ))}
                </ul>
              </div>
            ) : null}
            {resolution.denied.length > 0 ? (
              <div>
                <div className="text-xs font-semibold text-red-800">
                  Hidden
                </div>
                <ul className="mt-1 space-y-1">
                  {resolution.denied.map((e) => (
                    <EntitlementRow key={e.appId} e={e} />
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </Collapsible>

      <Collapsible title="Why might an additional app not be discovered?">
        <ul className="list-disc space-y-1 pl-5 text-xs text-slate-700">
          <li>
            <b>CIAM / External ID:</b> Microsoft Graph does not expose{" "}
            <code>/me/appRoleAssignments</code> or{" "}
            <code>/servicePrincipals</code> to customer identities. These
            calls return 403 <code>Authorization_RequestDenied</code> by
            design. Populate <code>VITE_APPS_JSON</code> in{" "}
            <code>wrangler.jsonc</code> instead.
          </li>
          <li>
            <b>Workforce:</b> The app's Enterprise Application has
            "Visible to users?" set to <i>No</i> (adds{" "}
            <code>HideApp</code> tag) or "Assignment required?" is{" "}
            <i>Yes</i> and the user lacks an assignment.
          </li>
          <li>
            <b>Missing permission:</b> The SPA app registration lacks
            delegated <code>Application.Read.All</code>; enrichment
            fails but the assignment is still counted.
          </li>
          <li>
            <b>Token scope:</b> The user hasn't consented to the
            required Graph scopes (<code>User.Read.All</code>,{" "}
            <code>Application.Read.All</code>).
          </li>
          <li>
            <b>Entitlement gate:</b> Tile failed a required role,
            group or claim check configured in{" "}
            <code>VITE_APPS_JSON</code>. See the "Hidden" list above
            for the exact reason.
          </li>
        </ul>
      </Collapsible>

      <div className="mt-3 text-right text-[10px] text-slate-400">
        Captured at {new Date(diagnostics.capturedAt).toLocaleString()}
      </div>
    </section>
  );
}

function EntitlementRow({ e }: { e: AppEntitlement }) {
  return (
    <li className="rounded border border-slate-200 bg-white p-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900">{e.app.name}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-500">{e.app.category}</span>
        {e.app.assignedToMe ? (
          <span className="chip bg-emerald-100 text-emerald-800">Graph</span>
        ) : (
          <span className="chip bg-slate-100 text-slate-700">config</span>
        )}
        {e.app.assignmentRequired ? (
          <span className="chip bg-amber-100 text-amber-800">
            assignment required
          </span>
        ) : null}
      </div>
      {e.reasons.length ? (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-slate-600">
          {e.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function Collapsible({
  title,
  defaultOpen,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="mb-3 rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs font-semibold text-slate-800 hover:bg-slate-50"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        {title}
      </button>
      {open ? <div className="border-t border-slate-100 p-3">{children}</div> : null}
    </div>
  );
}

function KV({ k, v }: { k: string; v?: string | null }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-xs">
      <dt className="col-span-1 text-slate-500">{k}</dt>
      <dd className="col-span-2 break-all font-mono text-slate-800">
        {v || <span className="italic text-slate-400">(empty)</span>}
      </dd>
    </div>
  );
}
